import { AnalyticsCards } from "Common/Components/AnalyticsCards/AnalyticsCards";
import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import DynamicTabs from "components/DynamicTabs";
import { ENQ } from "Constants/StatusConstant";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import { CheckCircle, FileText, FolderOpen, PhoneCall } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import {
  exportEnquiryCSV,
  fetchProductEnquiries,
  fetchServiceEnquiries,
} from "slices/enquiry/thunk";

import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { useResetTableOnTabChange } from "CustomHooks/useResetTableOnTabChange";
import { Add } from "@mui/icons-material";
import { MecordColors } from "Constants/Color";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import { defaultInnerStyle } from "Common/Components/PageContainer/PageContainer";
import SearchBox from "Common/Components/SearchBox/SearchBox";

interface MasterListPageProps {
  activeType: "product" | "service";
  activeTab: string | undefined;
  title: string;
  addButtonLabel: string;
  addRoute: string;
  columns: any[];
  statusToggleUrl: string;
  reloadFlag?: boolean;
}

const MasterListPage: React.FC<MasterListPageProps> = ({
  title,
  addButtonLabel,
  addRoute,
  columns,
  statusToggleUrl,
  activeTab,
  activeType,
  reloadFlag,
}) => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const prevTabRef = useRef<string | undefined>(undefined);

  const tableState = usePreservedTableState({
    key: TableKeys.ENQUIRY.PRODUCT,
    tabString: activeTab,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  useResetTableOnTabChange(TableKeys.ENQUIRY.PRODUCT, activeTab, resetState);

  // ✅ Select enquiry state
  const selectEnquiries = createSelector(
    (state: any) => state?.Enquiry,
    (enquiry) => ({
      list:
        activeType === "product" ? enquiry?.productList : enquiry?.serviceList,
      rowCount: enquiry.rowCount,
      loading: enquiry.loading,
      error: enquiry.error,
      todaysFollowUpCount: enquiry.todaysFollowUpCount,
    }),
  );

  // Static Card Data
  const statuses = [
    {
      label: "Total Enquiries",
      icon: <FileText size={16} />,
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      count: 120,
    },
    {
      label: "Active Enquiries",
      icon: <FolderOpen size={16} />,
      bgColor: "bg-red-50",
      textColor: "text-red-600",
      count: 35,
    },
    {
      label: "Converted",
      icon: <CheckCircle size={16} />,
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-600",
      count: 50,
    },
    {
      label: "Pending Follow-ups",
      icon: <PhoneCall size={16} />,
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      count: 82,
    },
  ];

  const { rowCount, loading, error, todaysFollowUpCount } =
    useSelector(selectEnquiries);

  // Get permissions based on activeType (product or service)
  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.enquiry?.app_name,
    activeType === "product"
      ? app_modules?.enquiry?.modules?.product
      : app_modules?.enquiry?.modules?.service,
  );

  // ✅ Local state
  const [data, setData] = useState<any[]>([]);
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);
  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>();
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [tableInstance, setTableInstance] = useState<any>();
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // ✅ Fetch list
  const fetchEnquiryData = async () => {
    try {
      if (activeType === "product") {
        const resData = await dispatch(
          fetchProductEnquiries({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: filterValue,
            stage: activeTab || ENQ.ENQUIRY_LOG,
          }),
        ).unwrap();

        setData(resData.data);
      } else {
        const resData = await dispatch(
          fetchServiceEnquiries({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: filterValue,
            stage: activeTab,
          }),
        ).unwrap();

        setData(resData.data);
      }
    } catch (error: any) {
      console.error("Enquiry fetch failed:", error);

      // optional UX
      // toast.error(error?.message || "Failed to fetch enquiries");
      setData([]); // prevent UI crash
    }
  };

  useEffect(() => {
    fetchEnquiryData();
  }, [
    activeType,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
    activeTab,
    dispatch,
    reloadFlag,
  ]);

  // useEffect(() => {
  //   if (!list || !Array.isArray(list)) return;

  //   const formatted = list.map((row: any) => ({
  //     id: row.enquiry_details?.id,
  //     enquiry_number: row.enquiry_details?.enquiry_number,
  //     enquiry_date: row.enquiry_details?.enquiry_date,
  //     company_name: row.enquiry_details?.company_name,
  //     source_of_enquiry: row.enquiry_details?.source_of_enquiry,
  //     agent: row.enquiry_details?.agent?.name || "N/A",
  //     reference: row.enquiry_details?.reference || "N/A",
  //     items: row.item_details?.length || 0,
  //     status: row.enquiry_details?.status || "N/A",
  //     emd: row.enquiry_details?.emd || "-",
  //     assigned_to: row.assigned_member_details?.map((m: any) => ({
  //       id: m.id,
  //       user_name: m.user_name || m.user,
  //     })),
  //     notes: row.enquiry_details?.notes || "",
  //     created_by_user: row.item_details?.[0]?.created_by_user || "N/A",
  //     created_at: row.item_details?.[0]?.created_at || null,
  //     updated_by_user: row.item_details?.[0]?.updated_by_user || "N/A",
  //     updated_at: row.item_details?.[0]?.updated_at || null,
  //   }));

  //   setData(formatted);
  // }, [list]);

  // ✅ Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  const handleExport = async (): Promise<{ blob: Blob; filename: string }> => {
    return await exportEnquiryCSV({
      search,
      filters: filterValue,
      type: activeType,
      stage: activeTab,
    });
  };

  function handleChange(e: any): void {
    setSearchInput(e.target.value);
  }


  return (
    <div
      className="page-content px-3 enquiry-list-page"
      style={{
        paddingTop: "calc(var(--vz-header-height, 56px) + 4px)",
        paddingBottom: "6px",
        height: "100vh",
        boxSizing: "border-box",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
      }}
    >
      <style>
        {`
          .enquiry-list-page .enquiry-toolbar-title {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            min-height: 34px;
            margin-bottom: 0;
          }

          .enquiry-list-page .enquiry-toolbar-title h6 {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
            line-height: 1.2;
            display: flex;
            align-items: center;
            height: 34px;
          }

          .enquiry-list-page .enquiry-toolbar-title .custom-action-button,
          .enquiry-list-page .enquiry-toolbar-title .btn {
            height: 34px !important;
            min-height: 34px !important;
            padding: 0 12px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            line-height: 1 !important;
            font-size: 13px !important;
          }

          .enquiry-list-page .enquiry-tabs-wrap {
            margin-top: 0;
            margin-bottom: 4px;
          }

          .enquiry-list-page .enquiry-tabs-wrap .MuiTabs-root {
            min-height: 30px;
          }

          .enquiry-list-page .enquiry-tabs-wrap .MuiTab-root {
            min-height: 30px !important;
            padding-top: 2px !important;
            padding-bottom: 2px !important;
          }

          .enquiry-list-page .enquiry-search-row {
            margin: 0 !important;
            --bs-gutter-y: 0.25rem;
          }

          .enquiry-list-page .enquiry-search-row .search-box .form-control.search {
            height: 32px !important;
            min-height: 32px !important;
            padding-top: 0.2rem !important;
            padding-bottom: 0.2rem !important;
            font-size: 13px !important;
          }

          .enquiry-list-page .enquiry-search-row .custom-export-button,
          .enquiry-list-page .enquiry-search-row .custom-column-visibility-button {
            height: 32px !important;
            min-height: 32px !important;
            padding: 0 10px !important;
          }

          /* Give search more width on this page */
          .enquiry-list-page .enquiry-search-row > .col-lg-4 {
            flex: 0 0 45%;
            max-width: 45%;
          }
          .enquiry-list-page .enquiry-search-row > .col-xl-5 {
            flex: 0 0 48%;
            max-width: 48%;
          }
          @media (min-width: 992px) {
            .enquiry-list-page .enquiry-search-row > [class*="col-lg-8"],
            .enquiry-list-page .enquiry-search-row > [class*="col-xl-7"] {
              flex: 1 1 auto;
              max-width: none;
              width: auto;
            }
          }
        `}
      </style>

      <div className="flex-shrink-0">
        <AnalyticsCards statuses={statuses} />
      </div>

      <div
        className="d-flex flex-column enquiry-panel"
        style={{
          ...defaultInnerStyle,
          padding: "10px 14px 10px",
          flex: "1 1 0",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <div className="flex-shrink-0">
          <div className="enquiry-toolbar-title">
            <h6>{title}</h6>

            {canCreate && (
              <ActionButton
                label={addButtonLabel}
                icon={<Add fontSize="small" />}
                navigateLink={addRoute}
                variant="primary"
                style={{
                  backgroundColor: MecordColors.PrimaryButtonBGColor,
                  borderColor: MecordColors.PrimaryButtonBGColor,
                  borderRadius: "8px",
                }}
              />
            )}
          </div>

          <div className="enquiry-tabs-wrap">
            <DynamicTabs
              activeTab={activeTab}
              activeType={activeType}
              basePath="enquiry"
              tabs={[
                { value: "enquiry_log", label: "Enquiry Log" },
                { value: "draft", label: "Draft" },
              ]}
            />
          </div>

          <Row className="align-items-center enquiry-search-row g-2">
            <SearchBox
              value={searchInput}
              onChange={(e: any) => handleChange(e)}
              placeholder="Search..."
            />

            <Col
              xs={12}
              md={12}
              lg={8}
              xl={7}
              className="d-flex justify-content-end align-items-center gap-2"
            >
              {tableInstance && (
                <ColumnVisibilityDropdown
                  columns={tableInstance.getAllLeafColumns()}
                  excludedColumns={["action", "sr_no"]}
                />
              )}
              {canView && <ExportButton onExport={handleExport} />}
            </Col>
          </Row>
        </div>

        <div
          className="enquiry-table-scroll"
          style={{
            flex: "1 1 0",
            maxHeight: "100%",
            minHeight: 0,
            marginTop: 4,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <style>
            {`
              /* Hug content when few rows; scroll only when it exceeds available space */
              .enquiry-list-page .enquiry-table-scroll .table-responsive.table-card {
                flex: 0 1 auto;
                max-height: 100%;
                min-height: 0;
                margin: 0 !important;
                padding: 0;
                overflow: auto !important;
                border: 1px solid #e5e7eb;
                border-radius: 6px;
              }

              .enquiry-list-page .enquiry-table-scroll .table-responsive .table {
                border-collapse: separate;
                border-spacing: 0;
                margin-bottom: 0;
                width: 100%;
              }

              /* Restore proper cell padding on all sides */
              .enquiry-list-page .enquiry-table-scroll .table thead th,
              .enquiry-list-page .enquiry-table-scroll .table tbody td {
                padding: 10px 12px !important;
                vertical-align: middle;
              }

              .enquiry-list-page .enquiry-table-scroll .table thead th:first-child,
              .enquiry-list-page .enquiry-table-scroll .table tbody td:first-child {
                padding-left: 14px !important;
              }

              .enquiry-list-page .enquiry-table-scroll .table thead th:last-child,
              .enquiry-list-page .enquiry-table-scroll .table tbody td:last-child {
                padding-right: 14px !important;
              }

              /* Keep pagination outside the table scroll with breathing room */
              .enquiry-list-page .enquiry-table-scroll > .row {
                flex-shrink: 0;
                margin-top: 0.5rem !important;
                margin-bottom: 0 !important;
                padding: 4px 6px 8px !important;
              }

              /* Sticky header within the same scroll container as sticky left/right cols */
              .enquiry-list-page .enquiry-table-scroll .table-responsive thead th {
                position: sticky;
                top: 0;
                z-index: 3;
                background-color: ${MecordColors?.LightThemeBGColor || "#f8e8e8"} !important;
              }

              .enquiry-list-page .enquiry-table-scroll .table-responsive thead .sticky-first-column {
                z-index: 12 !important;
                left: 0;
              }

              .enquiry-list-page .enquiry-table-scroll .table-responsive thead .sticky-column {
                z-index: 12 !important;
                right: 0;
              }

              .enquiry-list-page .enquiry-table-scroll .table-responsive tbody .sticky-first-column {
                z-index: 11;
              }

              .enquiry-list-page .enquiry-table-scroll .table-responsive tbody .sticky-column {
                z-index: 10;
              }
            `}
          </style>
          <LoaderErrorWrapper
            loading={loading || isStatusUpdateLoading}
            error={error}
          >
            <MasterTableContainer
              columns={columns}
              data={data || []}
              totalRecords={rowCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              sorting={sorting}
              onSortingChange={setSorting}
              isBordered={false}
              isPagination={true}
              isGlobalFilter={false}
              SearchPlaceholder={`Search ${title.toLowerCase()}...`}
              manualSorting={true}
              colValue={setFilterValue}
              onTableReady={setTableInstance}
            />
          </LoaderErrorWrapper>
        </div>
      </div>
    </div>
  );
};

export default MasterListPage;
